import type { MouseEvent } from 'react';

import { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useClassy from '@core/hooks/useClassy';
import useMediaQuery from '@core/hooks/useMediaQuery';
import useUniqueId from '@core/hooks/useUniqueId';
import classy from '@core/utils/classy';
import { ThemeContext } from '@Hub/Theme';
import Button from '@ui/Button';
import Dropdown from '@ui/Dropdown';
import Icon from '@ui/Icon';
import Menu, { MenuHeader } from '@ui/Menu';
import MenuItem from '@ui/Menu/Item';

import { useDarkMode } from '../../utils/hooks';

import classes from './style.module.scss';

interface ThemeOverrideProps {
  className?: string;
  setTheme: (theme: string) => void;
}

/**
 * Update the color mode element that exists within the given document.
 */
function updateColorMode(document: Document, nextTheme: 'dark' | 'light' | 'system') {
  const colorModeElement = document.querySelector('[data-color-mode]');
  if (colorModeElement) {
    colorModeElement.setAttribute('data-color-mode', nextTheme);
  }
}

function updateParentColorMode(nextTheme: 'dark' | 'light' | 'system') {
  if (window.self === window.top) return;

  try {
    updateColorMode(window.parent.document, nextTheme);
  } catch {
    // Cross-origin embeds cannot access the parent document.
  }
}

const ThemeOverride = ({ className, setTheme }: ThemeOverrideProps) => {
  const { t } = useTranslation();
  const bem = useClassy(classes, 'ThemeToggle');
  const colorScheme = useDarkMode();
  const { colorMode, setColorMode } = useContext(ThemeContext);
  const [themeOverride, setThemeOverride] = useState(colorScheme);
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [visible, setVisible] = useState(false);
  const uid = useUniqueId('ThemeToggle');

  const applyTheme = useCallback(
    (nextTheme: 'dark' | 'light' | 'system') => {
      // except…when there’s a preview dark mode query param
      const params = new URLSearchParams(window.location.search);
      const previewDarkMode = params.get('previewDarkMode');

      if (!previewDarkMode) {
        // Update color mode on the current document, whether it's the main
        // window or within an iframe.
        updateColorMode(document, nextTheme);

        // When rendering inside an iframe, also update the parent document's
        // color mode to ensure the entire shell reflects the selected theme.
        updateParentColorMode(nextTheme);
      }

      localStorage.setItem('color-scheme', nextTheme);
      if (setColorMode) setColorMode(nextTheme);
      setTheme(nextTheme); // for header RDMD blocks (Owlbot)
    },
    [setColorMode, setTheme],
  );

  // hide until loaded
  useEffect(() => {
    if (colorMode === themeOverride) setVisible(true);
  }, [colorMode, themeOverride]);

  // set theme when toggled
  useEffect(() => {
    applyTheme(themeOverride);
  }, [applyTheme, themeOverride]);

  // set theme
  const toggleTheme = (theme: 'dark' | 'light' | 'system') => {
    setThemeOverride(theme);
    applyTheme(theme);
  };

  // quick toggle
  const quickToggleTheme = (event: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    event.preventDefault();
    let newTheme = themeOverride || 'system';

    if (newTheme === 'system') {
      newTheme = prefersDarkMode ? 'light' : 'dark';
    } else if (newTheme === 'light') {
      newTheme = 'dark';
    } else if (newTheme === 'dark') {
      newTheme = 'light';
    }
    setThemeOverride(newTheme);
    applyTheme(newTheme);
  };

  return (
    <Dropdown appendTo={() => document.body} className={classy(bem('-wrapper'), className)} clickInToClose sticky>
      <Button
        aria-label="Toggle color scheme"
        className={bem('rm-ThemeToggle', '&', visible && '_active')}
        ghost
        id={uid('button')}
        onContextMenu={e => quickToggleTheme(e)}
        size="sm"
      >
        <svg
          aria-labelledby={uid('button')}
          className={bem('Icon', '-Icon', colorMode === 'system' && '-Icon_active')}
          role="img"
          viewBox="0 0 24 24"
        >
          <path className={bem('-Icon-ray')} d="M12 2v2" />
          <path d="M14.837 16.385a6 6 0 1 1-7.223-7.222c.624-.147.97.66.715 1.248a4 4 0 0 0 5.26 5.259c.589-.255 1.396.09 1.248.715" />
          <path d="M16 12a4 4 0 0 0-4-4" />
          <path className={bem('-Icon-ray')} d="m19 5-1.256 1.256" />
          <path className={bem('-Icon-ray')} d="M20 12h2" />
        </svg>

        <svg
          aria-labelledby={uid('button')}
          className={bem('Icon', '-Icon', '-Icon_dark', colorMode === 'dark' && '-Icon_active')}
          role="img"
          viewBox="0 0 24 24"
        >
          <path d="M19 14.79C18.8427 16.4922 18.2039 18.1144 17.1582 19.4668C16.1126 20.8192 14.7035 21.8458 13.0957 22.4265C11.4879 23.0073 9.74798 23.1181 8.0795 22.7461C6.41102 22.3741 4.88299 21.5345 3.67423 20.3258C2.46546 19.117 1.62594 17.589 1.25391 15.9205C0.881876 14.252 0.992717 12.5121 1.57346 10.9043C2.1542 9.29651 3.18083 7.88737 4.53321 6.84175C5.8856 5.79614 7.5078 5.15731 9.21 5C8.21341 6.34827 7.73385 8.00945 7.85853 9.68141C7.98322 11.3534 8.70386 12.9251 9.8894 14.1106C11.0749 15.2961 12.6466 16.0168 14.3186 16.1415C15.9906 16.2662 17.6517 15.7866 19 14.79Z" />
          <path
            className={bem('-Icon-star')}
            d="M18.3707 1C18.3707 3.22825 16.2282 5.37069 14 5.37069C16.2282 5.37069 18.3707 7.51313 18.3707 9.74138C18.3707 7.51313 20.5132 5.37069 22.7414 5.37069C20.5132 5.37069 18.3707 3.22825 18.3707 1Z"
          />
        </svg>

        <svg
          aria-labelledby={uid('button')}
          className={bem('Icon', '-Icon', colorMode === 'light' && '-Icon_active')}
          role="img"
          viewBox="0 0 24 24"
        >
          <path className={bem('-Icon-ray')} d="M12 1V3" />
          <path className={bem('-Icon-ray')} d="M18.36 5.64L19.78 4.22" />
          <path className={bem('-Icon-ray')} d="M21 12H23" />
          <path className={bem('-Icon-ray')} d="M18.36 18.36L19.78 19.78" />
          <path className={bem('-Icon-ray')} d="M12 21V23" />
          <path className={bem('-Icon-ray')} d="M4.22 19.78L5.64 18.36" />
          <path className={bem('-Icon-ray')} d="M1 12H3" />
          <path className={bem('-Icon-ray')} d="M4.22 4.22L5.64 5.64" />
          <path d="M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z" />
        </svg>
      </Button>
      <Menu>
        <MenuHeader>{t('colorScheme.title')}</MenuHeader>
        <MenuItem
          active={colorMode === 'system'}
          icon={<Icon aria-hidden="true" name="sun-moon" />}
          onClick={() => toggleTheme('system')}
        >
          {t('colorScheme.system')}
        </MenuItem>
        <MenuItem
          active={colorMode === 'light'}
          icon={<Icon aria-hidden="true" name="sun" />}
          onClick={() => toggleTheme('light')}
        >
          {t('colorScheme.light')}
        </MenuItem>
        <MenuItem
          active={colorMode === 'dark'}
          icon={<Icon aria-hidden="true" name="moon-star" />}
          onClick={() => toggleTheme('dark')}
        >
          {t('colorScheme.dark')}
        </MenuItem>
      </Menu>
    </Dropdown>
  );
};

export default ThemeOverride;
